import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/AddOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import LineItemsDialog from '@/components/common/LineItemsDialog';
import SearchableSelect from '@/components/common/SearchableSelect';
import StatusChip from '@/components/common/StatusChip';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { PERMISSIONS } from '@/types/auth';
import type { ClientDto } from '@/types/materials';
import {
  BILLING_CHARGE_CODES,
  type ChargeCategory,
  type ClientContractDto,
  type ClientRateCardLineDto,
  type ClientRateCardLineRequest,
  type CreateClientContractRequest,
  type RateBasis,
  type UpdateClientContractRequest,
} from '@/types/billing';

const CHARGE_CATEGORIES: ChargeCategory[] = ['Storage', 'Handling', 'VAS'];
const RATE_BASES: RateBasis[] = ['PerPalletDay', 'PerSqFtDay', 'PerUnit', 'PerTransaction', 'Flat'];

// Storage/Handling ChargeCodes are a fixed, closed set the billing engine recognizes automatically
// (see the backend's BillingChargeCodes.cs) — offered as a dropdown here rather than free text so a
// rate line can never be silently ignored by the engine due to a typo. VAS is reference-only (the
// engine never looks up VAS rates from the contract — see BillingService.AddVasLinesAsync), so it
// stays free text.
const STORAGE_CODES = [{ value: BILLING_CHARGE_CODES.STORAGE_PER_DAY, label: 'Storage — per unit-day' }];
const HANDLING_CODES = [
  { value: BILLING_CHARGE_CODES.INBOUND_PER_GRN, label: 'Inbound — per GRN' },
  { value: BILLING_CHARGE_CODES.INBOUND_PER_UNIT, label: 'Inbound — per unit received' },
  { value: BILLING_CHARGE_CODES.OUTBOUND_PER_DISPATCH, label: 'Outbound — per dispatch order' },
  { value: BILLING_CHARGE_CODES.OUTBOUND_PER_UNIT, label: 'Outbound — per unit dispatched' },
];

const emptyLine: ClientRateCardLineRequest = {
  chargeCategory: 'Storage', chargeCode: BILLING_CHARGE_CODES.STORAGE_PER_DAY, description: null, basis: 'PerUnit',
  rate: 0, unitOfMeasure: null, minChargeAmount: null, gstRatePercent: 18, sacCode: null,
};

export default function ClientContractMaster() {
  const { t } = useTranslation('clientContractMaster');
  const queryClient = useQueryClient();

  const { data: contracts = [], isLoading } = useQuery({
    queryKey: ['client-contracts'],
    queryFn: async () => (await apiClient.get<ClientContractDto[]>('/client-contracts')).data,
  });
  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => (await apiClient.get<ClientDto[]>('/clients')).data,
  });

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ClientContractDto | null>(null);
  const [viewing, setViewing] = useState<ClientContractDto | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ClientContractDto | null>(null);

  const [clientId, setClientId] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [effectiveTo, setEffectiveTo] = useState('');
  const [currency, setCurrency] = useState('INR');
  const [status, setStatus] = useState<'Active' | 'Expired' | 'Cancelled'>('Active');
  const [lines, setLines] = useState<ClientRateCardLineRequest[]>([{ ...emptyLine }]);

  const resetForm = () => {
    setClientId('');
    setEffectiveFrom(new Date().toISOString().slice(0, 10));
    setEffectiveTo('');
    setCurrency('INR');
    setStatus('Active');
    setLines([{ ...emptyLine }]);
  };

  const openCreate = () => {
    setEditing(null);
    resetForm();
    setOpen(true);
  };

  const openEdit = (contract: ClientContractDto) => {
    setEditing(contract);
    setClientId(contract.clientId);
    setEffectiveFrom(contract.effectiveFrom.slice(0, 10));
    setEffectiveTo(contract.effectiveTo?.slice(0, 10) ?? '');
    setCurrency(contract.currency);
    setStatus(contract.status as 'Active' | 'Expired' | 'Cancelled');
    setLines(
      contract.rateLines.map((l) => ({
        chargeCategory: l.chargeCategory, chargeCode: l.chargeCode, description: l.description, basis: l.basis,
        rate: l.rate, unitOfMeasure: l.unitOfMeasure, minChargeAmount: l.minChargeAmount, gstRatePercent: l.gstRatePercent,
        sacCode: l.sacCode,
      })),
    );
    setOpen(true);
  };

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['client-contracts'] });

  const saveMutation = useMutation({
    mutationFn: () => {
      const cleanLines = lines.filter((l) => l.chargeCode);
      if (editing) {
        const payload: UpdateClientContractRequest = {
          contractNumber: editing.contractNumber, effectiveFrom, effectiveTo: effectiveTo || null,
          billingCycle: 'Monthly', status, currency, rateLines: cleanLines,
        };
        return apiClient.put(`/client-contracts/${editing.id}`, payload);
      }
      const payload: CreateClientContractRequest = {
        clientId, effectiveFrom, effectiveTo: effectiveTo || null, billingCycle: 'Monthly', currency, rateLines: cleanLines,
      };
      return apiClient.post('/client-contracts', payload);
    },
    onSuccess: () => {
      invalidate();
      setOpen(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/client-contracts/${id}`),
    onSuccess: () => {
      invalidate();
      setConfirmDelete(null);
    },
  });

  const columns: DataTableColumn<ClientContractDto>[] = [
    { field: 'contractNumber', headerName: t('table.contractNumber') },
    { field: 'clientName', headerName: t('table.client'), render: (r) => `${r.clientCode} — ${r.clientName}` },
    { field: 'effectiveFrom', headerName: t('table.effectiveFrom'), render: (r) => new Date(r.effectiveFrom).toLocaleDateString() },
    { field: 'effectiveTo', headerName: t('table.effectiveTo'), render: (r) => (r.effectiveTo ? new Date(r.effectiveTo).toLocaleDateString() : '—') },
    { field: 'rateLines', headerName: t('table.rateLines'), render: (r) => r.rateLines.length },
    { field: 'status', headerName: t('table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    {
      field: 'action', headerName: t('table.actions'), render: (r) => (
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button size="small" onClick={() => setViewing(r)}>
            {t('view')}
          </Button>
          <IfPermitted permission={PERMISSIONS.CLIENT_CONTRACT_MANAGE}>
            <Button size="small" onClick={() => openEdit(r)}>
              {t('edit')}
            </Button>
            <Button size="small" color="error" onClick={() => setConfirmDelete(r)}>
              {t('delete')}
            </Button>
          </IfPermitted>
        </Stack>
      ),
    },
  ];

  const lineColumns: DataTableColumn<ClientRateCardLineDto>[] = [
    { field: 'chargeCategory', headerName: t('form.chargeCategory') },
    { field: 'chargeCode', headerName: t('form.chargeCode') },
    { field: 'basis', headerName: t('form.basis') },
    { field: 'rate', headerName: t('form.rate') },
    { field: 'gstRatePercent', headerName: t('form.gstRatePercent') },
  ];

  const codeOptionsFor = (category: ChargeCategory) =>
    category === 'Storage' ? STORAGE_CODES : category === 'Handling' ? HANDLING_CODES : [];

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={
          <IfPermitted permission={PERMISSIONS.CLIENT_CONTRACT_MANAGE}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              {t('addContract')}
            </Button>
          </IfPermitted>
        }
      />

      <DataTable columns={columns} rows={contracts} loading={isLoading} searchable getRowId={(r) => r.id} exportFileName="client-contracts" />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? editing.contractNumber : t('addContract')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2}>
              <SearchableSelect
                label={t('form.client')} value={clientId} onChange={setClientId}
                fullWidth disabled={Boolean(editing)}
                options={clients.map((c) => ({ value: c.id, label: `${c.code} — ${c.name}` }))}
              />
              {editing && (
                <TextField select label={t('form.status')} value={status} onChange={(e) => setStatus(e.target.value as 'Active' | 'Expired' | 'Cancelled')} fullWidth>
                  <MenuItem value="Active">{t('form.statusActive')}</MenuItem>
                  <MenuItem value="Expired">{t('form.statusExpired')}</MenuItem>
                  <MenuItem value="Cancelled">{t('form.statusCancelled')}</MenuItem>
                </TextField>
              )}
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                type="date" label={t('form.effectiveFrom')} value={effectiveFrom} fullWidth
                InputLabelProps={{ shrink: true }} onChange={(e) => setEffectiveFrom(e.target.value)}
              />
              <TextField
                type="date" label={t('form.effectiveTo')} value={effectiveTo} fullWidth
                InputLabelProps={{ shrink: true }} onChange={(e) => setEffectiveTo(e.target.value)}
              />
              <TextField label={t('form.currency')} value={currency} onChange={(e) => setCurrency(e.target.value)} fullWidth />
            </Stack>

            <strong>{t('form.rateLines')}</strong>

            {lines.map((line, idx) => (
              <Stack key={idx} spacing={1} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: '2px' }}>
                <Stack direction="row" spacing={1}>
                  <TextField
                    select label={t('form.chargeCategory')} value={line.chargeCategory} size="small" sx={{ minWidth: 140 }}
                    onChange={(e) => {
                      const chargeCategory = e.target.value as ChargeCategory;
                      const firstCode = codeOptionsFor(chargeCategory)[0]?.value ?? '';
                      setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, chargeCategory, chargeCode: firstCode } : l)));
                    }}
                  >
                    {CHARGE_CATEGORIES.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </TextField>
                  {line.chargeCategory === 'VAS' ? (
                    <TextField
                      label={t('form.chargeCode')} value={line.chargeCode} size="small" fullWidth
                      onChange={(e) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, chargeCode: e.target.value } : l)))}
                    />
                  ) : (
                    <TextField
                      select label={t('form.chargeCode')} value={line.chargeCode} size="small" fullWidth
                      onChange={(e) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, chargeCode: e.target.value } : l)))}
                    >
                      {codeOptionsFor(line.chargeCategory).map((c) => (
                        <MenuItem key={c.value} value={c.value}>
                          {c.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                  <TextField
                    select label={t('form.basis')} value={line.basis} size="small" sx={{ minWidth: 140 }}
                    onChange={(e) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, basis: e.target.value as RateBasis } : l)))}
                  >
                    {RATE_BASES.map((b) => (
                      <MenuItem key={b} value={b}>
                        {b}
                      </MenuItem>
                    ))}
                  </TextField>
                  <IconButton size="small" onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))} disabled={lines.length <= 1}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <TextField
                    label={t('form.description')} value={line.description ?? ''} size="small" fullWidth
                    onChange={(e) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, description: e.target.value || null } : l)))}
                  />
                  <TextField
                    type="number" label={t('form.rate')} value={line.rate} size="small" sx={{ minWidth: 110 }}
                    onChange={(e) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, rate: Number(e.target.value) } : l)))}
                  />
                  <TextField
                    type="number" label={t('form.minChargeAmount')} value={line.minChargeAmount ?? ''} size="small" sx={{ minWidth: 110 }}
                    onChange={(e) =>
                      setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, minChargeAmount: e.target.value ? Number(e.target.value) : null } : l)))
                    }
                  />
                  <TextField
                    type="number" label={t('form.gstRatePercent')} value={line.gstRatePercent} size="small" sx={{ minWidth: 100 }}
                    onChange={(e) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, gstRatePercent: Number(e.target.value) } : l)))}
                  />
                  <TextField
                    label={t('form.sacCode')} value={line.sacCode ?? ''} size="small" sx={{ minWidth: 110 }}
                    onChange={(e) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, sacCode: e.target.value || null } : l)))}
                  />
                </Stack>
              </Stack>
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={() => setLines((prev) => [...prev, { ...emptyLine }])} sx={{ alignSelf: 'flex-start' }}>
              {t('form.addLine')}
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>{t('discardChanges')}</Button>
          <Button
            variant="contained"
            disabled={(!editing && !clientId) || lines.some((l) => !l.chargeCode || l.rate < 0) || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {t('saveContract')}
          </Button>
        </DialogActions>
      </Dialog>

      <LineItemsDialog
        open={Boolean(viewing)}
        onClose={() => setViewing(null)}
        title={viewing?.contractNumber ?? ''}
        subtitle={viewing ? `${viewing.clientCode} — ${viewing.clientName}` : undefined}
        columns={lineColumns}
        rows={viewing?.rateLines ?? []}
      />

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        message={t('deleteContract.message')}
        loading={deleteMutation.isPending}
        onConfirm={() => confirmDelete && deleteMutation.mutate(confirmDelete.id)}
        onClose={() => setConfirmDelete(null)}
      />
    </>
  );
}
